// src/polaris-components/GroupedManagePlanPage.tsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { reactivateSubscription } from '../utils/subscriptionApi';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import '../styles/grouped-manage-plan.css';

function formatAthleteLimit(maxAthletes?: number | null): string {
  if (maxAthletes == null) return 'N/A';
  if (maxAthletes === -1) return 'Unlimited';
  return maxAthletes.toLocaleString();
}

function formatSubscriptionPrice(
  amount: number,
  currency: string,
  interval: 'month' | 'year' | 'day',
  intervalCount?: number | null
): string {
  const { amount: formattedAmount, interval: formattedInterval } = formatSubscriptionPriceParts(
    amount,
    currency,
    interval,
    intervalCount
  );
  return `${formattedAmount}${formattedInterval}`;
}

function formatSubscriptionPriceParts(
  amount: number,
  currency: string,
  interval: 'month' | 'year' | 'day',
  intervalCount?: number | null
): { amount: string; interval: string } {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount);
  const count = intervalCount && intervalCount > 1 ? intervalCount : 1;
  if (count > 1) {
    return {
      amount: formattedAmount,
      interval: ` / ${count} ${interval}${count === 1 ? '' : 's'}`,
    };
  }
  const unit = interval === 'year' ? 'year' : interval === 'day' ? 'day' : 'month';
  return {
    amount: formattedAmount,
    interval: `/${unit}`,
  };
}

function formatBillingCycleLabel(
  interval: 'month' | 'year' | 'day',
  intervalCount?: number | null
): string {
  const count = intervalCount && intervalCount > 1 ? intervalCount : 1;
  if (count > 1) {
    const unit = interval === 'year' ? 'years' : interval === 'day' ? 'days' : 'months';
    return `Every ${count} ${unit}`;
  }
  if (interval === 'year') return 'Annual billing';
  if (interval === 'day') return 'Daily billing';
  return 'Monthly billing';
}

function formatSubscriptionPeriodRange(
  start?: string | null,
  end?: string | null
): string | null {
  const formatShortDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  if (start && end) {
    return `${formatShortDate(start)} – ${formatShortDate(end)}`;
  }
  if (end) {
    return `Through ${formatShortDate(end)}`;
  }
  if (start) {
    return `Started ${formatShortDate(start)}`;
  }
  return null;
}

function humanizeMetadataKey(key: string): string {
  const spaced = key
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export const GroupedManagePlanPage: React.FC = () => {
  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch,
  } = useSubscription();

  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const navigate = useNavigate();

  const metadataEntries = useMemo(() => {
    const metadata = subscription?.displayMetadata || {};
    return Object.entries(metadata)
      .filter(([key, value]) => key.trim() && String(value).trim())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([key, value]) => ({
        label: humanizeMetadataKey(key),
        value: String(value).trim(),
      }));
  }, [subscription?.displayMetadata]);

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const handleCancelSuccess = () => {
    refetch();
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      await reactivateSubscription();
      setShowSuccessBanner(true);
      await refetch();
    } catch {
      // Error handling - silently fail
    } finally {
      setReactivateLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (subscriptionLoading) {
    return (
      <div className="grouped-manage-plan-page">
        <div className="grouped-manage-plan-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="grouped-manage-plan-page">
        <div className="grouped-manage-plan-error">
          <h2>Error Loading Subscription</h2>
          <button onClick={refetch} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="grouped-manage-plan-page">
        <div className="no-subscription-state">
          <div className="empty-icon">💳</div>
          <h2>No Active Subscription</h2>
          <p>You don&apos;t have an active subscription yet. Choose a plan to unlock all features!</p>
          <button onClick={() => navigate('/signup')} className="cta-btn" data-testid="choose-plan-btn">
            Choose a Plan
          </button>
        </div>
      </div>
    );
  }

  const isOrganization = subscription.planCategory === 'organization';
  const displayPlanName =
    isOrganization && subscription.organizationDisplayName
      ? `${subscription.organizationDisplayName} Plan`
      : subscription.planName;
  const isCancelled = !!subscription.cancelAtPeriodEnd;
  const athleteLimitLabel =
    subscription.maxAthletes === -1
      ? 'Unlimited student-athletes'
      : `Up to ${formatAthleteLimit(subscription.maxAthletes)} student-athletes`;
  const priceParts = formatSubscriptionPriceParts(
    subscription.amount,
    subscription.currency,
    subscription.interval,
    subscription.intervalCount
  );
  const billingCycleLabel = formatBillingCycleLabel(
    subscription.interval,
    subscription.intervalCount
  );
  const subscriptionPeriodLabel = formatSubscriptionPeriodRange(
    subscription.currentPeriodStart,
    subscription.currentPeriodEnd
  );

  return (
    <div className="grouped-manage-plan-page" data-testid="grouped-manage-plan-page">
      {showSuccessBanner && (
        <div className="success-banner" data-testid="success-banner">
          <div className="success-content">
            <div className="success-icon">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.3333 4L6 11.3333L2.66666 8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="success-text">
              You&apos;ve successfully reactivated your <strong>{displayPlanName}</strong> plan.
            </div>
          </div>
          <button
            className="banner-close"
            onClick={() => setShowSuccessBanner(false)}
            aria-label="Close banner"
          >
            ✕
          </button>
        </div>
      )}

      <div className="grouped-manage-plan-header">
        <div className="header-content">
          <p>
            You can cancel anytime. Your account will remain active until your current billing period
            ends, so you won&apos;t lose access right away.
          </p>
        </div>
        {!isCancelled && (
          <button
            className="cancel-subscription-btn"
            onClick={handleCancelSubscription}
            data-testid="cancel-subscription-btn"
          >
            Cancel subscription
          </button>
        )}
        {isCancelled && (
          <button
            className="cancel-subscription-btn reactivate-header-btn"
            onClick={handleReactivate}
            disabled={reactivateLoading}
            data-testid="reactivate-btn"
          >
            {reactivateLoading ? 'Processing...' : 'Reactivate'}
          </button>
        )}
      </div>

      <div className="current-plan-section" data-testid="current-plan-section">
        <h2>Current Plan</h2>
        <div className="current-plan-card" data-testid="current-plan-card">
          <div className="current-plan-card-main">
            <div className="current-plan-card-heading">
              <h3 className="current-plan-name" data-testid="current-plan-name">
                {displayPlanName}
              </h3>
              {!isCancelled && (
                <span className="current-plan-status-badge">Active</span>
              )}
            </div>
            <p className="current-plan-limit" data-testid="current-plan-limit">
              {athleteLimitLabel}
            </p>
          </div>

          <div className="current-plan-card-pricing">
            <div className="current-plan-price" data-testid="current-plan-price">
              <span className="current-plan-price-amount">{priceParts.amount}</span>
              <span className="current-plan-price-interval">{priceParts.interval}</span>
            </div>
            <p className="current-plan-duration" data-testid="current-plan-duration">
              <span className="current-plan-billing-cycle">{billingCycleLabel}</span>
              {subscriptionPeriodLabel && (
                <>
                  <span className="current-plan-duration-separator" aria-hidden="true">
                    ·
                  </span>
                  <span className="current-plan-period">{subscriptionPeriodLabel}</span>
                </>
              )}
            </p>
          </div>

          {metadataEntries.length > 0 && (
            <dl className="current-plan-metadata" data-testid="current-plan-metadata">
              {metadataEntries.map((entry) => (
                <div className="current-plan-metadata-row" key={entry.label}>
                  <dt>{entry.label}</dt>
                  <dd>{entry.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {isCancelled && subscription.currentPeriodEnd && (
            <div className="cancellation-notice" data-testid="cancellation-notice">
              Your subscription will end on {formatDate(subscription.currentPeriodEnd)}
            </div>
          )}
        </div>
      </div>

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSuccess={handleCancelSuccess}
        currentPeriodEnd={subscription?.currentPeriodEnd}
      />
    </div>
  );
};
